import React, {useState, useMemo, useEffect} from "react";
import {useQuery} from "react-query";
import {apiV2} from "../../../api/axiosConfig";
import {ClientAPIKey} from "./types";
import {PaginatedResponse} from "../../../types/PaginatedResponse";
import LoadingSpinner from "../../../components/ui/LoadingSpinner";
import PaginationNoneFloating from "../../../components/ui/Content/PaginationNoneFloating";
import {Link, useNavigate, useSearchParams} from "react-router-dom";
import {BlueButton} from "../../../components/ui/Buttons";
import Modal from "../../../components/portal/Modal";
import {useAuth} from "../../../context/user";
import {ClientSelectSingle} from "../../../components/form-v2/searchable-select/ClientSelect";
import {Client} from "../../../types/userManagement";
import BasicText from "../../../components/form-v2/basic-text";
import {Action, ActionSelect} from "../../../components/ui/ActionSelect";
import {isAxiosError} from "axios";
import SearchIcon from "@mui/icons-material/Search";

const APIKeyList: React.FC = () => {
    const auth = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const [page, setPage] = useState(Number(searchParams.get("page")) || 1);
    const [search, setSearch] = useState(searchParams.get("search") || "");
    const [client, setClient] = useState<Client | undefined>();
    const [actionError, setActionError] = useState("");
    const navigate = useNavigate();


    useEffect(() => {
        console.log(auth.profile)
        if (!auth.developerAccess) {
            navigate("/");
        }
    }, [auth.developerAccess]);

    const fetchAPIKeys = async (): Promise<PaginatedResponse<ClientAPIKey>> => {
        const params: any = {
            page,
            limit: 10,
        };

        if (auth.isSuperAdmin && client?.id) {
            params.clientID = client.id;
        }

        if (search) {
            params.search = search;
        }

        const response = await apiV2.get<PaginatedResponse<ClientAPIKey>>("client-api-keys", {params});
        return response.data;
    };

    const enableQuery = useMemo(() => {
        if (auth.isSuperAdmin) {
            return !!client;
        }
        return true;
    }, [auth.isSuperAdmin, client]);

    const {data: apiKeys, isLoading, refetch} = useQuery(
        ["client-api-keys", client?.id, page, search],
        fetchAPIKeys,
        {enabled: enableQuery}
    );

    return (
        <div className="flex flex-col gap-8">
            {actionError && (
                <div
                    className="text-white bg-red shadow-md p-4 flex flex-row gap-8 items-center justify-center rounded-md">
                    {actionError}
                </div>
            )}

            <div className="bg-white mt-4 flex flex-row gap-8 items-center">
                <h2 className="text-xl font-semibold">API Keys</h2>
                <div className="flex-1"/>
                <Link to="/settings/api-keys/create" className="w-32">
                    <BlueButton text="Create API Key"/>
                </Link>
            </div>

            {auth.isSuperAdmin && ( <div className="bg-white p-4 flex flex-row flex-wrap gap-8">

                    <div className="w-64">
                        <ClientSelectSingle
                            client={client}
                            onChange={(c) => {
                                setClient(c);
                                setPage(1);
                            }}
                        />
                    </div>

            </div>)}

            <div className="relative w-full">
                <div className="h-full absolute right-4 flex items-center">
                    <SearchIcon className="text-light-grey"/>
                </div>
                <input className="bg-white py-4 px-8 rounded-md outline outline-2 outline-light-light-grey w-full"

                       type="search"
                       placeholder="Search..."
                       onChange={(e) => {
                           setSearch(e.target.value);
                           setPage(1);
                       }}
                       defaultValue={""}
                />
            </div>

            <div className="bg-white shadow-md p-4">
                {isLoading ? (
                    <div className="flex justify-center p-8">
                        <LoadingSpinner/>
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-[25%_15%_15%_15%_15%_15%] mb-4 font-semibold">
                            <div>Name</div>
                            <div>Key ID</div>
                            <div>Expires</div>
                            <div>Status</div>
                            <div>Ed25519</div>
                            <div>Actions</div>
                        </div>
                        <hr className="my-4"/>
                        <div className="flex flex-col gap-4">
                            {apiKeys?.items && apiKeys.items.map((apiKey) => (
                                <APIKeyRow key={apiKey.id} apiKey={apiKey} refetch={refetch}
                                           setActionError={setActionError}/>
                            ))}
                        </div>
                        <div className="flex justify-center mt-4">
                            <PaginationNoneFloating
                                currentPage={page}
                                itemsPerPage={10}
                                dataLength={apiKeys?.total || 0}
                                togglePageSelect={setPage}
                            />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

interface APIKeyRowProps {
    apiKey: ClientAPIKey;
    refetch: () => void;
    setActionError: (error: string) => void;
}

const APIKeyRow: React.FC<APIKeyRowProps> = ({apiKey, refetch, setActionError}) => {
    const [showDelete, setShowDelete] = useState(false);
    const [showToggleDisable, setShowToggleDisable] = useState(false);
    const [showEd25519Modal, setShowEd25519Modal] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [toggling, setToggling] = useState(false);
    const [updatingEd25519, setUpdatingEd25519] = useState(false);
    const [ed25519PublicKey, setEd25519PublicKey] = useState(apiKey.ed25519PublicKey || "");
    const [ed25519Enabled, setEd25519Enabled] = useState(apiKey.ed25519Enabled);

    const expiresAt = new Date(apiKey.expiresAt);
    const isExpired = expiresAt < new Date();

    const deleteAPIKey = async () => {
        setDeleting(true);
        try {
            await apiV2.delete(`client-api-keys/${apiKey.id}`);
            refetch();
            setShowDelete(false);
        } catch (e) {
            console.error(e);
            if (isAxiosError(e) && e?.response?.data?.error) {
                setActionError(e.response.data.error);
            }
        }
        setDeleting(false);
    };

    const toggleDisabled = async () => {
        setToggling(true);
        try {
            await apiV2.patch(`client-api-keys/${apiKey.id}`, {
                disabled: !apiKey.disabled,
            });
            refetch();
            setShowToggleDisable(false);
        } catch (e) {
            console.error(e);
            if (isAxiosError(e) && e?.response?.data?.error) {
                setActionError(e.response.data.error);
            }
        }
        setToggling(false);
    };

    const updateEd25519 = async () => {
        setUpdatingEd25519(true);
        try {
            await apiV2.patch(`client-api-keys/${apiKey.id}`, {
                ed25519PublicKey: ed25519PublicKey.trim() || null,
                ed25519Enabled: ed25519Enabled,
            });
            refetch();
            setShowEd25519Modal(false);
        } catch (e) {
            console.error(e);
            if (isAxiosError(e) && e?.response?.data?.error) {
                setActionError(e.response.data.error);
            }
        }
        setUpdatingEd25519(false);
    };

    return (
        <>
            <Modal open={showDelete} setOpen={setShowDelete}>
                <div className="bg-white shadow-md p-4 flex flex-col gap-4 rounded-md">
                    <p className="font-semibold">Delete API Key</p>
                    <p>Are you sure you want to delete the API key &quot;{apiKey.name}&quot;?</p>
                    <p className="text-sm text-grey">This action cannot be undone.</p>
                    <BlueButton text="Delete API Key" loading={deleting} onClick={deleteAPIKey}/>
                </div>
            </Modal>

            <Modal open={showToggleDisable} setOpen={setShowToggleDisable}>
                <div className="bg-white shadow-md p-4 flex flex-col gap-4 rounded-md">
                    <p className="font-semibold">{apiKey.disabled ? "Enable" : "Disable"} API Key</p>
                    <p>
                        Are you sure you want to {apiKey.disabled ? "enable" : "disable"} the API
                        key &quot;{apiKey.name}&quot;?
                    </p>
                    <BlueButton
                        text={apiKey.disabled ? "Enable API Key" : "Disable API Key"}
                        loading={toggling}
                        onClick={toggleDisabled}
                    />
                </div>
            </Modal>

            <Modal open={showEd25519Modal} setOpen={setShowEd25519Modal}>
                <div className="bg-white shadow-md p-4 flex flex-col gap-4 rounded-md max-w-2xl">
                    <p className="font-semibold">Manage Ed25519 Signing</p>

                    <div className="flex flex-col gap-2">
                        <label className="font-semibold">Ed25519 Public Key</label>
                        <textarea
                            value={ed25519PublicKey}
                            onChange={(e) => setEd25519PublicKey(e.target.value)}
                            placeholder="Paste your base64-encoded Ed25519 public key (32 bytes)"
                            className="border border-light-grey rounded p-2 font-mono text-sm"
                            rows={3}
                        />
                        <p className="text-sm text-grey">
                            Leave empty to remove Ed25519 signing from this key
                        </p>
                    </div>

                    <div className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            id={`ed25519Enabled-${apiKey.id}`}
                            checked={ed25519Enabled}
                            onChange={(e) => setEd25519Enabled(e.target.checked)}
                            className="w-4 h-4"
                        />
                        <label htmlFor={`ed25519Enabled-${apiKey.id}`} className="text-sm">
                            Enable Ed25519 signature validation
                        </label>
                    </div>

                    <BlueButton
                        text="Update Ed25519 Settings"
                        loading={updatingEd25519}
                        onClick={updateEd25519}
                    />
                </div>
            </Modal>

            <div className="grid grid-cols-[25%_15%_15%_15%_15%_15%]">
                <div className="flex items-center">{apiKey.name}</div>
                <div className="flex items-center font-mono text-sm">
                    <div className="truncate pr-4">
                        {apiKey.keyID}
                    </div>
                </div>
                <div className="flex items-center">
                    {expiresAt.toLocaleDateString()}
                </div>
                <div className="flex items-center">
                    {apiKey.disabled ? (
                        <span className="text-red">Disabled</span>
                    ) : isExpired ? (
                        <span className="text-dark-orange">Expired</span>
                    ) : (
                        <span className="text-green">Active</span>
                    )}
                </div>
                <div className="flex items-center">
                    {apiKey.ed25519Enabled ? (
                        <span className="text-green text-sm">Enabled</span>
                    ) : apiKey.ed25519PublicKey ? (
                        <span className="text-grey text-sm">Disabled</span>
                    ) : (
                        <span className="text-grey text-sm">Not set</span>
                    )}
                </div>
                <div>
                    <ActionSelect label="Select Action">
                        <Action label={apiKey.disabled ? "Enable" : "Disable"}
                                onSelect={() => setShowToggleDisable(true)}/>
                        <Action label="Manage Ed25519" onSelect={() => setShowEd25519Modal(true)}/>
                        <Action label="Delete" onSelect={() => setShowDelete(true)}/>
                    </ActionSelect>
                </div>
            </div>
        </>
    );
};

export default APIKeyList;
